import React, { useState } from "react";
import { Link } from "react-router-dom";
import { urls } from "../../../business/routes/urls";
import { Icon } from "../icons";
import {
  avatarColor,
  fmtDate,
  fmtDateTime,
  initials,
  LOCALE,
  moveTypeLabel,
  tagBadgeClass,
} from "../lib/format";
import { isHourlyJob } from "../lib/hourly";
import { statusLabel } from "../lib/jobStatus";
import { useMcOps } from "../lib/McOpsContext";
import { RevisionList } from "../components/Common";
import { JobActions } from "../modal/JobActions";
import { buildTabs, JobTabBody, TAB_IDS } from "../modal/jobTabs";

/**
 * A job's own page (/mc-ops-dashboard/job/<id>): the popup's sections in a wide
 * work area, with the job's key facts pinned on the left. Same data, actions
 * and saving as the popup.
 */

const SERVICE_DATE_LABELS = [
  ["fullPackingDate", "Full packing"],
  ["partialPackingDate", "Partial packing"],
  ["packingDate", "Packing"],
  ["loadingDate", "Loading"],
  ["localDeliveryDate", "Local delivery"],
  ["unpackingDate", "Unpacking"],
];

const money = (v) => "$" + Number(v || 0).toLocaleString(LOCALE);

const Fact = ({ label, children }) =>
  children === "" || children === null || children === undefined ? null : (
    <div className="jp-fact">
      <span className="jp-fact-label">{label}</span>
      <span className="jp-fact-value">{children}</span>
    </div>
  );

const BackLink = () => (
  <Link className="jp-back" to={urls.mcOpsDashboard}>
    <Icon name="reply" />
    Back to board
  </Link>
);

export const JobPage = ({ jobId, initialTab = "s1", loading = false }) => {
  const { state, actions } = useMcOps();
  const [activeTab, setActiveTab] = useState(
    TAB_IDS.includes(initialTab) ? initialTab : "s1",
  );

  const job = (state.jobs || []).find((j) => j.id === jobId);

  // The board arrives after the first render: say "loading", not "not found".
  if (!job && loading) {
    return (
      <div className="job-page">
        <header className="jp-head">
          <BackLink />
        </header>
        <div className="jp-missing">Loading job…</div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-page">
        <header className="jp-head">
          <BackLink />
        </header>
        <div className="jp-missing">
          This job is not on your board. It may belong to another coordinator, or its
          move date is outside the dashboard's range.
        </div>
      </div>
    );
  }

  const isLD = job.moveType === "ld";
  const tabs = buildTabs(isLD);
  const serviceDates = job.serviceDates || {};
  const vipTags = (job.tags || []).filter((t) => /VIP/i.test(t));
  const reminders = job.reminders || [];
  const recentNotes = [...(job.notes || [])]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);
  const revisions = job.revisions || [];

  return (
    <div className="job-page">
      <header className="jp-head">
        <BackLink />
        <div className="jp-avatar" style={{ background: avatarColor(job.moveType) }}>
          {initials(job.clientName)}
        </div>
        <div className="jp-title">
          <h1>
            {job.clientName} — {job.zohoId}
            {isHourlyJob(job) ? "  ·  HOURLY JOB" : ""}
          </h1>
          <div className="jp-subtitle">
            <span className={`badge ${tagBadgeClass(moveTypeLabel(job.moveType))}`}>
              <span className="bdot" />
              {moveTypeLabel(job.moveType)}
            </span>
            {vipTags.map((t) => (
              <span className="badge vip" key={t}>
                <span className="bdot" />
                {t}
              </span>
            ))}
            {job.priority ? <span className={`jp-priority prio-${job.priority}`} /> : null}
            <span className="jp-status">{statusLabel(job.status)}</span>
            <span className="jp-move">Move {fmtDate(job.moveDate)}</span>
          </div>
        </div>
        <div className="jp-head-actions">
          {job.zohoUrl ? (
            <button
              type="button"
              className="btn small"
              title="Open this lead in Zoho CRM"
              onClick={() => window.open(job.zohoUrl, "_blank", "noopener")}
            >
              <Icon name="external" />
              Open in Zoho
            </button>
          ) : null}
          <JobActions
            job={job}
            isLD={isLD}
            activeTab={activeTab}
            actions={actions}
            dropUp={false}
          />
        </div>
      </header>

      <div className="jp-body">
        <aside className="jp-facts">
          <section className="jp-card">
            <div className="jp-card-title">Key facts</div>
            <Fact label="Status">{statusLabel(job.status)}</Fact>
            <Fact label="Salesperson (Zoho)">{job.salesperson || "—"}</Fact>
            <Fact label="Move date">{fmtDate(job.moveDate)}</Fact>
            {SERVICE_DATE_LABELS.map(([key, label]) =>
              serviceDates[key] ? (
                <Fact label={label} key={key}>
                  {fmtDate(serviceDates[key])}
                </Fact>
              ) : null,
            )}
            {isLD && job.deliveryWindowStart ? (
              <Fact label="Delivery window">
                {fmtDate(job.deliveryWindowStart)} – {fmtDate(job.deliveryWindowEnd)}
              </Fact>
            ) : null}
            {job.bolPickupSignedAt ? (
              <Fact label="Pickup BOL signed">{fmtDateTime(job.bolPickupSignedAt)}</Fact>
            ) : null}
            {job.bolDeliverySignedAt ? (
              <Fact label="Delivery BOL signed">{fmtDateTime(job.bolDeliverySignedAt)}</Fact>
            ) : null}
            <Fact label="From">{job.moveFrom}</Fact>
            <Fact label="To">{job.moveTo}</Fact>
            <Fact label="Price">{money(job.price)}</Fact>
            <Fact label="Size">{job.totalCubicFeet}</Fact>
            <Fact label="Phone">{job.phone}</Fact>
            <Fact label="Email">{job.email}</Fact>
            <Fact label="Source">{job.source}</Fact>
            <Fact label="Intro call">{job.introCallDone ? "Done" : "Not yet"}</Fact>
            <Fact label="Confirmation">{job.confirmCallDone ? "Confirmed" : "Not yet"}</Fact>
          </section>

          {revisions.length ? (
            <section className="jp-card jp-card-revised">
              <RevisionList
                job={job}
                onReviewed={(text) => actions.markRevisionReviewed(job.id, text)}
              />
            </section>
          ) : null}

          <section className="jp-card">
            <div className="jp-card-title">Reminders</div>
            {reminders.length ? (
              reminders.map((r, i) => (
                <div className="jp-list-item" key={i}>
                  {r.text}
                  <div className="meta">{fmtDate(r.date)}</div>
                </div>
              ))
            ) : (
              <div className="jp-empty">No reminders set.</div>
            )}
          </section>

          <section className="jp-card">
            <div className="jp-card-title">Recent notes</div>
            {recentNotes.length ? (
              recentNotes.map((n, i) => (
                <div className="jp-list-item" key={i}>
                  {n.text}
                  <div className="meta">
                    {n.author} · {n.date ? fmtDateTime(n.date) : ""}
                  </div>
                </div>
              ))
            ) : (
              <div className="jp-empty">No notes yet.</div>
            )}
          </section>
        </aside>

        <main className="jp-work">
          <div className="jp-tabs" role="tablist">
            {tabs.map((t) => (
              <button
                type="button"
                role="tab"
                aria-selected={t.id === activeTab}
                className={`mtab-btn${t.id === activeTab ? " active" : ""}`}
                key={t.id}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="jp-tab-body">
            <JobTabBody
              tab={activeTab}
              job={job}
              isLD={isLD}
              escalations={state.escalations || []}
              actions={actions}
            />
          </div>
        </main>
      </div>
    </div>
  );
};
